<script setup lang="ts">
import { BadgeCheckIcon, ExclamationIcon } from '@heroicons/vue/outline';
import { watchDebounced } from '@vueuse/core';
import type { PropType } from 'vue';
import type { Collection } from '~/types/store';
import { getApiRoot } from '~/utilities/api';

const props = defineProps({
  modelValue: { type: Object as PropType<Collection>, required: true },
  profileId: { type: String, required: true },
});
const emit = defineEmits(['update:modelValue', 'onSave']);

const collection = toRef(props, 'modelValue');

const fetchingLicenseInProgress = ref<boolean>(false);
const licenseError = ref<string | undefined>(undefined);
watch(
  () => collection.value.licenseUrl,
  () => {
    if (!fetchingLicenseInProgress.value && collection.value.licenseHash !== undefined) {
      collection.value.licenseHash = undefined;
    }
  }
);
watchDebounced(
  () => collection.value.licenseUrl,
  async (licenseUrl) => {
    console.log(licenseUrl);

    if (licenseUrl && licenseUrl.startsWith('http')) {
      fetchingLicenseInProgress.value = true;
      try {
        const { hash } = await $fetch<{ hash: string }>(`${getApiRoot()}/studio/calculate_hash`, {
          method: 'post',
          body: { url: licenseUrl },
        });
        collection.value.licenseHash = hash;
        licenseError.value = undefined;
        return;
      } catch (error: any) {
        licenseError.value = error.error?.message;
      } finally {
        fetchingLicenseInProgress.value = false;
      }
    } else {
      licenseError.value = undefined;
    }
    collection.value.licenseHash = null;
  },
  { debounce: 300 }
);
</script>
<template>
  <div class="mt-4 flex w-full max-w-xl flex-col gap-6">
    <!--    <div>-->
    <!--      <label for="id" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"> ID </label>-->
    <!--      <div class="mt-1">-->
    <!--        <input-->
    <!--          id="id"-->
    <!--          v-model="collection.id"-->
    <!--          type="text"-->
    <!--          required-->
    <!--          name="id"-->
    <!--          autocomplete="id"-->
    <!--          class="block w-full rounded-md border-neutral-300 dark:border-neutral-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-neutral-800 dark:bg-neutral-800 dark:text-white dark:text-white sm:text-sm"-->
    <!--        />-->
    <!--      </div>-->
    <!--      <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">-->
    <!--        Only change this if you know what you are doing.-->
    <!--      </p>-->
    <!--    </div>-->
    <div>
      <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"
        >Collection Name
      </label>
      <div class="mt-1">
        <input
          id="name"
          v-model="collection.name"
          type="text"
          required
          name="name"
          autocomplete="name"
          class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
        />
      </div>
    </div>

    <div>
      <label for="description" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
        Collection Description
      </label>
      <div class="mt-1">
        <textarea
          id="description"
          v-model="collection.description"
          name="description"
          rows="5"
          class="block w-full rounded-md border border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
        />
      </div>
    </div>

    <div>
      <label class="flex gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200">
        License URL

        <div class="pointer-events-none flex items-center border-neutral-300 pr-3 dark:border-neutral-600">
          <svg
            v-if="fetchingLicenseInProgress"
            class="h-5 w-5 animate-spin text-emerald-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <BadgeCheckIcon
            v-else-if="collection.licenseUrl && collection.licenseHash"
            class="h-5 w-5 text-emerald-600"
          />
          <ExclamationIcon
            v-else-if="collection.licenseUrl && collection.licenseHash === null"
            class="h-5 w-5 text-yellow-600"
          />
        </div>
      </label>
      <div class="mt-1">
        <GobyUrlInputWithUpload v-model="collection.licenseUrl" :profile-id="profileId" name="license" />
      </div>
      <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        By attaching a license, buyers of your NFTs will directly see what rights they will receive when owning your
        NFTs. <br />
        More details and examples can be found on
        <NuxtLink
          class="font-medium text-emerald-600"
          target="_blank"
          to="https://docs.mintgarden.io/mintgarden-studio/create-a-collection#license-url"
          >docs.mintgarden.io
        </NuxtLink>
        .
      </p>
      <p v-if="licenseError" id="license-error" class="mt-2 text-sm text-red-600">
        {{ licenseError }}
      </p>
    </div>
    <!--    <div>-->
    <!--      <label for="twitterHandle" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">-->
    <!--        Twitter Handle-->
    <!--      </label>-->
    <!--      <div class="relative mt-1 rounded-md shadow-sm">-->
    <!--        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">-->
    <!--          <span class="text-neutral-500 dark:text-neutral-400 sm:text-sm">@</span>-->
    <!--        </div>-->
    <!--        <input-->
    <!--          id="twitterHandle"-->
    <!--          v-model="collection.twitterHandle"-->
    <!--          type="text"-->
    <!--          name="twitterHandle"-->
    <!--          autocomplete="twitterHandle"-->
    <!--          class="block w-full rounded-md border-neutral-300 dark:border-neutral-600 pl-8 focus:border-emerald-500 focus:ring-emerald-500 dark:bg-neutral-800 dark:text-white sm:text-sm"-->
    <!--          placeholder="MintGarden_io"-->
    <!--        />-->
    <!--      </div>-->
    <!--    </div>-->
    <!--    <div>-->
    <!--      <label for="website" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"> Website </label>-->
    <!--      <div class="mt-1">-->
    <!--        <input-->
    <!--          id="website"-->
    <!--          v-model="collection.website"-->
    <!--          type="text"-->
    <!--          name="website"-->
    <!--          autocomplete="website"-->
    <!--          placeholder="https://mintgarden.io"-->
    <!--          class="block w-full rounded-md border-neutral-300 dark:border-neutral-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-neutral-800 dark:text-white sm:text-sm"-->
    <!--        />-->
    <!--      </div>-->
    <!--    </div>-->

    <div>
      <label for="iconUrl" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
        Icon URL (image with 1:1 ratio)
      </label>
      <div class="mt-1">
        <GobyUrlInputWithUpload v-model="collection.iconUrl" :profile-id="profileId" name="icon" accept="image/*" />
      </div>
      <div v-if="collection.iconUrl" :class="['mt-1 flex h-48 cursor-pointer justify-start rounded-md']">
        <img :src="collection.iconUrl" class="object-contain" />
      </div>
    </div>
    <div>
      <label for="bannerUrl" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
        Banner URL (image with 4:1 ratio)
      </label>
      <div class="mt-1">
        <GobyUrlInputWithUpload v-model="collection.bannerUrl" :profile-id="profileId" name="banner" accept="image/*" />
      </div>
      <div v-if="collection.bannerUrl" :class="['mt-1 flex h-48 cursor-pointer justify-start rounded-md']">
        <img :src="collection.bannerUrl" class="object-contain" />
      </div>
    </div>
  </div>
</template>
